import { useState } from 'react';
import { MultifactorProps } from './UserModels';
import { api } from '#shared/ApiClient';

function Multifactor({
  authAppEnabled,
  maskedPhoneNumber,
  setConfirmationMessage,
  onRequestEmailPassword,
  enableRecovery = true,
  handleCancel = undefined,
  handleFormSubmit = HandleAuthCodeSubmit,
}: MultifactorProps) {
  const smsEnabled = !!maskedPhoneNumber;
  const [selectedMethod, setSelectedMethod] = useState(authAppEnabled ? 'app' : smsEnabled ? 'sms' : 'recovery');
  const [smsConfirmationChecked, setSmsConfirmationChecked] = useState(false);
  const [smsSendCodeDisabled, setSmsSendCodeDisabled] = useState(true);
  const [submitCodeDisabled, setSubmitCodeDisabled] = useState(false);
  const [authAppVerificationCode, setAuthAppVerificationCode] = useState('');
  const [smsVerificationCode, setSmsVerificationCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitCodeDisabled(true);

    const data = {
      SmsVerificationCode: smsVerificationCode,
      AuthAppVerificationCode: authAppVerificationCode,
      RecoveryCode: recoveryCode,
    };

    const result = await handleFormSubmit(data);

    if (result.ConfirmationMessage) {
      setConfirmationMessage(result.ConfirmationMessage);
    }

    if (result.ErrorMessage) {
      setErrorMessage(result.ErrorMessage);
      setSubmitCodeDisabled(false);
    }

    if (result.MultifactorReset) {
      setSubmitCodeDisabled(false);
      onRequestEmailPassword();
    }
  };

  const handleCancelClick = (event) => {
    event.preventDefault();
    handleCancel?.();
  };

  return (
    <form action="/dashboard/SignIn/MultifactorCode" method="post" onSubmit={handleSubmit}>
      <div className="form-group form-inline" style={{ textAlign: 'left' }}>
        {authAppEnabled && (
          <div className="form-check" style={{ paddingTop: '1em' }}>
            <input
              type="radio"
              id="radioApp"
              name="radioMethodGroup"
              onChange={() => {
                setSelectedMethod('app');
                setErrorMessage('');
              }}
              checked={selectedMethod === 'app'}
            />
            <label htmlFor="radioApp" style={{ marginLeft: '5px' }}>
              Authentication App
            </label>
            {selectedMethod === 'app' && (
              <input
                value={authAppVerificationCode}
                onChange={(e) => {
                  setAuthAppVerificationCode(e.target.value);
                  setErrorMessage('');
                }}
                autoComplete="off"
                autoFocus={selectedMethod === 'app'}
                className="form-control"
                id="appInput"
                name="AuthAppVerificationCode"
                placeholder="Enter your verification code"
                required
                style={{ display: 'block', marginTop: '20px', width: '250px' }}
                type="text"
              />
            )}
          </div>
        )}

        {smsEnabled && (
          <div className="form-check" style={{ paddingTop: '1em' }}>
            <input
              type="radio"
              id="radioSms"
              name="radioMethodGroup"
              onChange={() => setSelectedMethod('sms')}
              checked={selectedMethod === 'sms'}
            />
            <label htmlFor="radioSms" style={{ marginLeft: '5px' }}>
              SMS
            </label>
            {selectedMethod === 'sms' && (
              <>
                <div id="divDisclaimer" style={{ paddingTop: '20px' }}>
                  <h6 style={{ margin: '0 0 10px 0', textTransform: 'none' }}>Request Verification Code</h6>
                  <hr />
                  <p>A verification code will be sent to {maskedPhoneNumber} that will expire after 5 minutes.</p>
                  <div style={{ display: 'flex', paddingTop: '10px' }}>
                    <input
                      type="checkbox"
                      id="chkSmsDisclaimer"
                      checked={smsConfirmationChecked}
                      onChange={(e) => {
                        setSmsConfirmationChecked(e.target.checked);
                        setSmsSendCodeDisabled(!e.target.checked);
                      }}
                      style={{ marginRight: '10px' }}
                    />
                    <label htmlFor="chkSmsDisclaimer">
                      By selecting the text box to phone option, I consent to receive SMS text messages at this number
                      and understand that text messaging fees may be incurred from my mobile service provider.
                    </label>
                  </div>
                  <input
                    id="btnSendCode"
                    type="button"
                    className="btn-glow primary"
                    style={{ textTransform: 'none', float: 'right' }}
                    disabled={smsSendCodeDisabled}
                    value="Send Code"
                    onClick={
                      smsConfirmationChecked
                        ? () =>
                            SendSmsCode(
                              () => {
                                setSmsConfirmationChecked(false);
                                setSmsSendCodeDisabled(true);
                              },
                              setConfirmationMessage,
                              setErrorMessage
                            )
                        : undefined
                    }
                  />
                </div>
                <div id="divSendCode" style={{ paddingTop: '15px' }}>
                  <input
                    value={smsVerificationCode}
                    onChange={(e) => {
                      setSmsVerificationCode(e.target.value);
                      setErrorMessage('');
                    }}
                    autoComplete="off"
                    className="form-control"
                    id="smsInput"
                    name="SmsVerificationCode"
                    placeholder="Enter your verification code"
                    style={{ display: 'block', marginTop: '20px', width: '250px', marginLeft: 'inherit' }}
                    type="text"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {enableRecovery && (
          <div className="form-check" style={{ paddingTop: '1em' }}>
            <div className="radio">
              <span>
                <input
                  type="radio"
                  id="radioRecovery"
                  name="radioMethodGroup"
                  onChange={() => {
                    setSelectedMethod('recovery');
                    setErrorMessage('');
                  }}
                  checked={selectedMethod === 'recovery'}
                />
              </span>
            </div>
            <label htmlFor="radioRecovery" style={{ marginLeft: '5px' }}>
              Use Recovery Code
            </label>
            {selectedMethod === 'recovery' && (
              <>
                <div id="divNote">
                  Note: Using recovery code will remove MFA from your account and log you out. To regain access to your
                  account, you will need to log back in and follow the steps to re-enroll in MFA.
                </div>
                <input
                  value={recoveryCode}
                  onChange={(e) => {
                    setRecoveryCode(e.target.value);
                    setErrorMessage('');
                  }}
                  autoComplete="off"
                  autoFocus={selectedMethod === 'recovery'}
                  className="form-control"
                  id="rcInput"
                  name="RecoveryCode"
                  placeholder="Enter your recovery code"
                  style={{ display: 'block', marginTop: '20px', width: '250px' }}
                  type="text"
                />
              </>
            )}
          </div>
        )}
      </div>
      <hr style={{ marginLeft: '5%', marginRight: '5%' }} />
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {handleCancel && (
        <button
          type="button"
          id="mfa-cancel-btn"
          className="btn-glow secondary signup"
          style={{ marginRight: '10px' }}
          onClick={handleCancelClick}
        >
          Cancel
        </button>
      )}
      <input
        id="Submit"
        type="submit"
        className="btn-glow primary signup"
        value="Verify"
        disabled={submitCodeDisabled}
      />
    </form>
  );
}

async function SendSmsCode(onComplete, setConfirmationMessage, setErrorMessage) {
  const result = await api.post('SignIn/TextMultifactorSendCode', {});
  setConfirmationMessage(result.ConfirmationMessage);
  setErrorMessage(result.ErrorMessage);
  onComplete();
}

const HandleAuthCodeSubmit = async (data) => {
  const result = await api.post('SignIn/MultifactorCode', data);

  if (result.Authenticated) {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const redirectUrl = urlParams.get('ReturnUrl');
    MixpanelTracking?.trackPageLoad(redirectUrl);
    const destination = redirectUrl != null ? redirectUrl : 'Transactions';
    globalThis.location.href = destination;
  }

  return result;
};

export default Multifactor;
